import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { requestPermission, listenForMessages } from '../firebase';

// ============================================================
// تكوين Axios مع التوكن
// ============================================================
const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// إضافة التوكن تلقائياً لكل الطلبات
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================================
// المكون الرئيسي
// ============================================================
const CaptainDashboard = () => {
  const [activeTab, setActiveTab] = useState('main');
  const [isOnline, setIsOnline] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [stats, setStats] = useState({
    totalRides: 0,
    completedRides: 0,
    totalEarnings: 0,
    todayEarnings: 0,
    monthEarnings: 0,
    rating: 5,
  });
  const [availableRides, setAvailableRides] = useState([]);
  const [captainInfo, setCaptainInfo] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);

  // الحصول على معرف المستخدم
  const rawUser = localStorage.getItem('user');
  const userId = rawUser ? Number(JSON.parse(rawUser)?.id) : 1;

  // ============================================================
  // جلب البيانات من السيرفر
  // ============================================================
  const fetchCaptainData = useCallback(async () => {
    try {
      console.log('📡 جلب بيانات الكابتن للمستخدم:', userId);

      // جلب الإحصائيات
      try {
        const statsRes = await api.get(`/captains/stats/${userId}`);
        if (statsRes.data) {
          setStats(prev => ({
            ...prev,
            totalRides: statsRes.data.total_rides || 0,
            completedRides: statsRes.data.completed_rides || 0,
            totalEarnings: statsRes.data.total_earnings || 0,
            rating: statsRes.data.avg_rating || 5,
          }));
        }
      } catch (e) {
        console.log('⚠️ خطأ في جلب الإحصائيات:', e.message);
      }

      // جلب معلومات الكابتن
      try {
        const infoRes = await api.get(`/captains/info/${userId}`);
        if (infoRes.data) {
          setCaptainInfo(infoRes.data);
          setIsOnline(infoRes.data.is_online || false);
        }
      } catch (e) {
        console.log('⚠️ خطأ في جلب معلومات الكابتن:', e.message);
      }

    } catch (error) {
      console.log('❌ خطأ عام في جلب البيانات:', error);
    }
  }, [userId]);

  // ============================================================
  // جلب الرحلات المتاحة
  // ============================================================
  const fetchAvailableRides = useCallback(async () => {
    try {
      const res = await api.get('/rides/available');
      if (res.data && Array.isArray(res.data)) {
        setAvailableRides(res.data);
      }
    } catch (e) {
      // 404 يعني لا توجد رحلات، هذا طبيعي
      if (e.response?.status !== 404) {
        console.log('⚠️ خطأ في جلب الرحلات:', e.message);
      }
      setAvailableRides([]);
    }
  }, []);

  // ============================================================
  // إعداد FCM والإشعارات
  // ============================================================
  const setupFCM = useCallback(async () => {
    try {
      console.log('🔔 جاري إعداد FCM للمستخدم:', userId);
      
      const token = await requestPermission();
      if (token) {
        console.log('📱 FCM Token:', token.substring(0, 30) + '...');
        setFcmToken(token);

        // تسجيل التوكن في السيرفر
        try {
          await api.post('/notifications/register-token', {
            userId: userId,
            token: token,
            device: 'web'
          });
          console.log('✅ تم تسجيل التوكن في السيرفر');
        } catch (e) {
          console.log('⚠️ خطأ في تسجيل التوكن:', e.message);
        }
      } else {
        console.log('⚠️ لم يتم الحصول على FCM Token');
      }

      // الاستماع للإشعارات الواردة
      listenForMessages((payload) => {
        console.log('📨 إشعار ورد:', payload);
        if (payload?.data) {
          // إضافة الرحلة الجديدة إلى القائمة
          setAvailableRides(prev => {
            // تجنب التكرار
            const exists = prev.some(r => r.id === payload.data.rideId);
            if (exists) return prev;
            return [payload.data, ...prev];
          });
          // تحديث القائمة من السيرفر
          fetchAvailableRides();
        }
      });

    } catch (e) {
      console.log('❌ خطأ في إعداد FCM:', e);
    }
  }, [userId, fetchAvailableRides]);

  // ============================================================
  // تبديل حالة الاتصال
  // ============================================================
  const handleToggle = async (e) => {
    if (e) e.preventDefault();
    setLoadingStatus(true);

    const nextStatus = !isOnline;
    console.log(`📡 تغيير حالة الاتصال إلى: ${nextStatus ? '🟢 متصل' : '🔴 غير متصل'}`);

    try {
      const response = await api.post('/captains/toggle-status', {
        userId: userId,
        status: nextStatus
      });

      console.log('✅ رد السيرفر:', response.data);
      setIsOnline(nextStatus);

      // إذا أصبح متصلاً، جلب الرحلات
      if (nextStatus) {
        await fetchAvailableRides();
      } else {
        setAvailableRides([]);
      }

    } catch (error) {
      console.error('❌ فشل تغيير الحالة:', error);
      // عرض خطأ للمستخدم
      alert('⚠️ حدث خطأ في تغيير حالة الاتصال. حاول مرة أخرى.');
    } finally {
      setLoadingStatus(false);
    }
  };

  // ============================================================
  // قبول الرحلة
  // ============================================================
  const handleAcceptRide = async (rideId) => {
    try {
      await api.post('/rides/accept', {
        rideId: rideId,
        captainId: userId
      });
      alert('✅ تم قبول الرحلة بنجاح!');
      // تحديث القائمة
      fetchAvailableRides();
      fetchCaptainData();
    } catch (error) {
      console.error('❌ خطأ في قبول الرحلة:', error);
      alert('⚠️ حدث خطأ في قبول الرحلة');
    }
  };

  // ============================================================
  // التأثيرات (Effects)
  // ============================================================
  useEffect(() => {
    fetchCaptainData();
    setupFCM();
  }, [fetchCaptainData, setupFCM]);

  // تحديث دوري للرحلات (كل 10 ثوانٍ)
  useEffect(() => {
    let interval;
    if (isOnline) {
      fetchAvailableRides();
      interval = setInterval(() => {
        fetchAvailableRides();
      }, 10000);
    } else {
      setAvailableRides([]);
    }
    return () => clearInterval(interval);
  }, [isOnline, fetchAvailableRides]);

  // ============================================================
  // عرض المكون
  // ============================================================
  return (
    <div style={{
      padding: '20px',
      fontFamily: 'sans-serif',
      direction: 'rtl',
      textAlign: 'right',
      backgroundColor: '#f9f9f9',
      minHeight: '100vh'
    }}>

      {/* ===== شريط الملاحة ===== */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        background: '#7e22ce',
        padding: '15px',
        color: '#fff',
        borderRadius: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px' }}>🚗 تطبيق سيدات - لوحة الكابتن</h2>
          <small>معرّف السائقة: {userId}</small>
        </div>
        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            onClick={() => setActiveTab('main')}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              background: activeTab === 'main' ? '#fff' : 'transparent',
              color: activeTab === 'main' ? '#7e22ce' : '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 'bold'
            }}
          >
            🏠 الرئيسية
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              background: activeTab === 'reports' ? '#fff' : 'transparent',
              color: activeTab === 'reports' ? '#7e22ce' : '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 'bold'
            }}
          >
            📊 الأداء
          </button>
        </div>
      </div>

      {/* ===== المحتوى الرئيسي ===== */}
      {activeTab === 'main' && (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>

          {/* ===== كارت الحالة ===== */}
          <div style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            textAlign: 'center',
            border: '1px solid #eee',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '16px', color: '#555' }}>
              الحالة الحالية: <strong>{isOnline ? '🟢 متصلة ومستعدة' : '🔴 غير متصلة بالخدمة'}</strong>
            </p>

            <button
              onClick={handleToggle}
              disabled={loadingStatus}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: isOnline ? '#ef4444' : '#22c55e',
                border: 'none',
                borderRadius: '12px',
                cursor: loadingStatus ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                marginTop: '10px',
                opacity: loadingStatus ? 0.7 : 1
              }}
            >
              {loadingStatus ? '⏳ جاري التحديث...' : isOnline ? '🛑 إيقاف استقبال الطلبات' : '🟢 تفعيل الاتصال الآن'}
            </button>
          </div>

          {/* ===== حالة FCM ===== */}
          <div style={{
            background: '#fff',
            padding: '15px',
            borderRadius: '12px',
            border: '1px solid #eee',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            <p style={{ margin: 0 }}>
              📱 FCM: {fcmToken ? '✅ مسجل' : '⏳ جاري التسجيل...'}
            </p>
          </div>

          {/* ===== رادار الطلبات ===== */}
          <div style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '16px',
            border: '1px solid #eee'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>📡 رادار الطلبات</h3>

            {!isOnline ? (
              <div style={{
                background: '#fffbeb',
                color: '#b45309',
                padding: '15px',
                borderRadius: '12px',
                border: '1px solid #fde68a',
                textAlign: 'center',
                fontSize: '14px'
              }}>
                ⚠️ اضغط <strong>"تفعيل الاتصال الآن"</strong> لاستقبال الطلبات
              </div>
            ) : availableRides.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#9ca3af',
                border: '2px dashed #e5e7eb',
                borderRadius: '12px'
              }}>
                🔄 الرادار يعمل... بانتظار طلب جديد
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {availableRides.map((ride) => (
                  <div
                    key={ride.id}
                    style={{
                      padding: '15px',
                      background: '#faf5ff',
                      border: '1px solid #e9d5ff',
                      borderRadius: '12px'
                    }}
                  >
                    <p style={{ margin: '0 0 5px 0', fontSize: '13px' }}>
                      📍 من: <strong>{ride.pickup_address || 'الموقع الحالي'}</strong>
                    </p>
                    <p style={{ margin: '0 0 10px 0', fontSize: '13px' }}>
                      🏁 إلى: <strong>{ride.dropoff_address || 'الوجهة'}</strong>
                    </p>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid #f3e8ff',
                      paddingTop: '10px'
                    }}>
                      <span style={{ color: '#7e22ce', fontWeight: 'bold' }}>
                        {ride.estimated_fare || 25} ريال
                      </span>
                      <button
                        onClick={() => handleAcceptRide(ride.id)}
                        style={{
                          background: '#22c55e',
                          color: '#fff',
                          border: 'none',
                          padding: '6px 20px',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        قبول 👍
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}

      {/* ===== تبويب التقارير ===== */}
      {activeTab === 'reports' && (
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          background: '#fff',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid #eee'
        }}>
          <h3 style={{ margin: '0 0 15px 0' }}>📊 تقرير الأداء</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span>🚗 إجمالي الأرباح</span>
              <strong style={{ color: '#7e22ce' }}>{stats.totalEarnings} ريال</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span>✅ الرحلات المنفذة</span>
              <strong>{stats.completedRides} رحلة</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span>📊 إجمالي الرحلات</span>
              <strong>{stats.totalRides} رحلة</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span>⭐ تقييم العميلات</span>
              <strong>{stats.rating} ⭐</strong>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CaptainDashboard;